import { i18n } from "../i18n/i18n";

/**
 * A `{ en, de }` blob from `accomodationdetail`, in the customer's language.
 *
 * ── Why this is a function and not an index ────────────────────────────────
 *
 * These blobs are OPTIONAL on every hand-created apartment and venue, so
 * `detail?.importantNotes[lang]` is not a safe read: optional-chaining down to
 * `importantNotes` and then indexing throws the moment the key is absent, and
 * the whole detail page white-screens. That is #2664, which hardened the venue
 * overview; the room-rental overview kept the unguarded form at two sites until
 * #1714's follow-up, and it needed no divided room to fire.
 *
 * Extracted from `venueReservation/ApartmentOverview.tsx`, where it was written
 * for #2664, rather than copied into the sibling: "check whether the logic
 * already exists before implementing" (CLAUDE.md). Both overviews now call this
 * one, so a future fix cannot land on only one of them — which is exactly how
 * the two drifted apart in the first place.
 *
 * Returns `""` for an absent value, so a caller can test `.length > 0` and
 * render NOTHING. An absent fact is not an empty label.
 */
export const getLocalizedText = (
  value?: { en?: string; de?: string } | null,
  /**
   * The language to resolve in. Defaults to the shared i18next instance, so
   * every call site reads exactly one language and none has to remember to
   * pass it.
   *
   * Explicit rather than only-implicit for two reasons: a component holding
   * `i18n` from `useTranslation()` can pass its own, and a test can name the
   * language instead of initialising an i18next instance to change it.
   */
  language?: string
): string => {
  if (!value) return "";
  const lang = (language ?? i18n.language)?.toLowerCase() ?? "en";
  // `de-DE` and `de` must resolve alike; the tenant config can set either.
  const normalized = lang.split("-")[0] as "en" | "de";
  return value[normalized] ?? value.en ?? value.de ?? "";
};
